import DocumentPage from './DocumentPage';

export const purchaseDocument = {
  slug: 'purchase',
  route: '/purchase',
  title: 'Purchase',
  category: 'Finance & Procurement',
  format: 'DOCX',
  description:
    'The Purchase module manages procurement from request for quotation through purchase orders, receipts, vendor bills, vendor management, and purchasing configuration.',
  metrics: [
    { label: 'Primary focus', value: 'Procurement workflow' },
    { label: 'Core records', value: 'RFQs, POs, receipts, bills' },
    { label: 'Financial closure', value: 'Vendor billing' },
  ],
  workflow: [
    'Create a request for quotation and convert it into a purchase order after vendor confirmation.',
    'Receive products in inventory and validate the receipt operation to update stock.',
    'Process the vendor bill and maintain vendor, product, pricelist, and category records for future purchasing.',
  ],
  sections: [
    {
      id: 'purchase-overview',
      title: 'Purchase Module Overview',
      body: [
        'The Purchase Module in Odoo 17 is used to manage the complete purchasing process of an organization.',
        'It helps businesses purchase products or services from vendors and track the entire procurement workflow efficiently.',
      ],
    },
    {
      id: 'rfq-and-purchase-order',
      title: 'Request for Quotation and Purchase Order',
      body: [
        'Go to Purchase -> Order -> Request for Quotation. The Request for Quotation is the first step in the procurement process.',
        'It allows you to send an inquiry to a vendor to get pricing and availability for specific products. Once the vendor confirms the quotation, the RFQ can be converted into a Purchase Order by clicking Confirm Order.',
      ],
      gallery: [
        {
          src: '/assets/doc-images/purchase/image.png',
          alt: 'Request for quotation form',
          caption: 'RFQ form with vendor, deadline, arrival date, and product details.',
        },
        {
          src: '/assets/doc-images/purchase/image2.png',
          alt: 'Confirmed purchase order',
          caption: 'Purchase Order created from the RFQ with lifecycle actions available.',
        },
      ],
      detailCards: [
        {
          title: 'Purchase order workflow actions',
          bullets: [
            'Receive Products: register the arrival of goods in the warehouse and update inventory levels.',
            'Send PO by Email: send the formal Purchase Order document directly to the vendor.',
            'Confirm Receipt Date: acknowledge and set the expected delivery date.',
            'Create Bill: generate a draft vendor bill in accounting.',
            'Cancel: void the purchase order if procurement is no longer required.',
            'Lock: prevent further manual changes to the Purchase Order.',
          ],
        },
      ],
    },
    {
      id: 'receipts-and-validation',
      title: 'Receipts and Validation',
      body: [
        'Once a Purchase Order is confirmed, Odoo automatically generates a receipt document such as WH/IN/00003 to manage the physical entry of goods into the warehouse.',
      ],
      gallery: [
        {
          src: '/assets/doc-images/purchase/image3.png',
          alt: 'Receipt in ready state',
          caption: 'Receipt document before validation with operation details.',
        },
        {
          src: '/assets/doc-images/purchase/image4.png',
          alt: 'Receipt in done state',
          caption: 'Receipt document after validation with stock updated.',
        },
      ],
      detailCards: [
        {
          title: 'Receipt action buttons',
          bullets: [
            'Validate: confirms goods arrival and officially increases stock levels.',
            'Print: generates a paper copy of the picking operations.',
            'Print Labels: prints product labels or barcodes for shelving.',
            'Cancel: voids the receipt if delivery is rejected or created in error.',
          ],
        },
        {
          title: 'Receipt status',
          bullets: [
            'Draft: receipt is created but not yet ready for processing.',
            'Ready: products are expected and the warehouse can receive them.',
            'Done: final stage once Validate has been clicked and stock is updated.',
          ],
        },
      ],
    },
    {
      id: 'vendor-bills',
      title: 'Vendor Bills',
      body: [
        'After receiving products or services, the organization must process the vendor invoice to complete the procurement cycle.',
      ],
      figure: {
        src: '/assets/doc-images/purchase/image5.png',
        alt: 'Vendor bill posted and paid',
        caption: 'Vendor Bill showing draft-to-posted lifecycle, payment ribbon, and invoice lines.',
      },
      detailCards: [
        {
          title: 'Action buttons',
          bullets: [
            'Credit Note: issue a refund or reverse the bill due to returned goods or pricing errors.',
            'Reset to Draft: move a posted bill back into draft to make corrections.',
          ],
        },
        {
          title: 'Bill details and accounting',
          bullets: [
            'Bill Reference: displays the unique identifier for the bill and vendor quote reference.',
            'Bill Date: the date the invoice was issued by the vendor.',
            'Accounting Date: the date the expense is officially recorded.',
            'Due Date: the payment deadline.',
            'Payment Reference: used to identify which order the payment covers.',
          ],
        },
        {
          title: 'Financial status and summary',
          bullets: [
            'Draft to Posted status controls accounting finalization.',
            'A green PAID ribbon appears once payment is registered and reconciled.',
            'Invoice lines show product, quantity, price, and applicable taxes.',
          ],
        },
      ],
    },
    {
      id: 'vendors-and-products',
      title: 'Vendors and Products',
      body: [
        'Go to Purchase -> Orders -> Vendors to manage supplier information such as contact details, address, payment terms, and pricing.',
        'Go to Purchase -> Products -> Products to manage all products that can be purchased from vendors, including product name, cost, vendor information, and purchase descriptions.',
      ],
      gallery: [
        {
          src: '/assets/doc-images/purchase/image6.png',
          alt: 'Vendor list or kanban view',
          caption: 'Vendor management screen for supplier records.',
        },
        {
          src: '/assets/doc-images/purchase/image7.png',
          alt: 'Purchasable products view',
          caption: 'Products view showing items available for purchasing.',
        },
      ],
    },
    {
      id: 'purchase-configuration',
      title: 'Purchase Configuration',
      body: [
        'The Purchase configuration area helps organizations maintain vendor pricing and product organization for efficient procurement.',
      ],
      subsections: [
        {
          id: 'vendor-pricelists',
          title: 'Vendor Pricelists',
          body: [
            'Go to Purchase -> Configuration -> Vendor Pricelists. This feature allows organizations to manage the prices offered by different vendors for specific products.',
            'It helps businesses compare vendor prices and choose the most suitable supplier.',
          ],
          figure: {
            src: '/assets/doc-images/purchase/image8.png',
            alt: 'Vendor pricelist list view',
            caption: 'Vendor Pricelist showing vendor, product, price, currency, and lead time.',
          },
        },
        {
          id: 'product-categories',
          title: 'Product Categories',
          body: [
            'Go to Purchase -> Configuration -> Product Categories. This feature allows users to organize products into different categories.',
            'Categorizing products helps businesses manage and analyze product data more efficiently.',
          ],
          figure: {
            src: '/assets/doc-images/purchase/image9.png',
            alt: 'Product categories list',
            caption: 'Product Categories used to organize purchasable items.',
          },
        },
      ],
    },
  ],
  related: ['inventory', 'invoice', 'expenses'],
};

export default function PurchasePage() {
  return <DocumentPage document={purchaseDocument} />;
}
